<script>
    import {onMount, untrack} from 'svelte';

    import {slide} from 'svelte/transition';
    import {cubicInOut} from 'svelte/easing';

    import classnames from 'clsx';

    import {userStore} from '@js/stores/userStore';
    import {mapStore} from '@js/stores/mapStore';

    import I18n from '@js/modules/translations';
    import * as Utils from '@js/modules/utils';

    import {
        ElevationRideTypes,
        RideLevels,
        RouteTypeToRideType,
        resolveMaxDistanceMeters
    } from '@js/components/modules/constants';
    import Notification from '@js/components/layouts/notification';
    import {pushError} from '@js/actions/errorManagement';

    import {MAP_CLASS, MAP_MODULES} from '@js/modules/maps/mapConstants';
    import MapBuilder from '@js/modules/maps/MapBuilder';
    import MapTileService from '@js/modules/maps/mapTileService';
    import {undoActionForKeydown} from '@js/modules/maps/undoShortcut';
    import MapToolbar from '@js/components/maps/MapToolbar.svelte';
    import MapRouteTypeBar from '@js/components/maps/MapRouteTypeBar.svelte';
    import MapLayers from '@js/components/maps/MapLayers.svelte';
    import MapTypeLegend from '@js/components/maps/MapTypeLegend.svelte';
    import MapModeBanner from '@js/components/maps/MapModeBanner.svelte';
    import MapLoader from '@js/components/maps/MapLoader.svelte';
    import MapDataOutline from '@js/components/maps/MapDataOutline.svelte';
    import MapElevationButton from '@js/components/maps/MapElevationButton.svelte';
    import MapElevationGraph from '@js/components/maps/MapElevationGraph.svelte';
    import MapElevationComputation from '@js/components/maps/MapElevationComputation.svelte';
    import MapSlopeLegend from '@js/components/maps/MapSlopeLegend.svelte';
    import MapSurfaceLegend from '@js/components/maps/MapSurfaceLegend.svelte';
    import MapClimbs from '@js/components/maps/MapClimbs.svelte';

    import StorageService from '@js/modules/storage';
    import AnalyticsService from '@js/modules/analyticsService';
    import Computation from '@js/modules/computation';

    const MapSectionPalette = () => import('@js/components/maps/MapSectionPalette.svelte');

    const {
        mapId,
        class: className,
        rideId,
        reusePreviousTracks = true,
        initialMarkerData,
        initialMapPoints,
        initialSections,
        initialSurfaces,
        movingDuration = false,
        onDistanceChange,
        // Reuse across surfaces (home / planner / ride new+edit) — defaults preserve home behaviour:
        // Route type is chosen via the top-of-map MapRouteTypeBar on every surface, so the toolbar's
        // own route-type control stays off (matching MapToolbar's own default).
        useRouteType = false,
        onPathStarted,                // page-specific "first point drawn" tracker (home vs planner)
        extraModules = [],            // extra map modules to load (e.g. planner ISOCHRONE)
        deferredExtraModules = [],    // of those, the ones to defer out of the initial load
        mapExtras                     // snippet rendered inside the map container (page-specific panels)
    } = $props();

    const TIME_STORAGE_KEY = 'MapTimeStore';
    const WEIGHT_STORAGE_KEY = 'MAPPLANNER-userWeight';
    const SECTION_PALETTE_COLORS = ['#e57373', '#ffb74d', '#fff176', '#4db6ac', '#64b5f6', '#ba68c8', '#a1887f'];
    const defaultSectionColor = $derived($mapStore.map?.getDefaultTrackColor?.() || '#327d1c');
    const sectionColors = $derived([defaultSectionColor, ...SECTION_PALETTE_COLORS]);

    const _getUserWeight = () => {
        return window.currentUserWeight || StorageService.getItem(WEIGHT_STORAGE_KEY);
    };

    let _previousMapDistance = 0;
    let _previousMapLocationValue;
    let _previousUnit = userStore.unit;
    let _previousRideType = mapStore.currentRideType;
    let _previousRouteType = mapStore.currentRouteType;
    let _hasInitialElevation = false;
    let _elevationModuleRetried = false;

    // A copied/shared ride inherits its source's stored difficulty + technicity (matching the ride-show
    // page) until the track is actually edited, instead of drifting to a fresh client recompute. Held
    // as {level, technicity} while inherited, null once the user edits. `_inheritedAwaitingLoad` lets the
    // initial load's distance-settle through without treating it as an edit.
    let _inheritedAnalysis = $state(null);
    let _inheritedAwaitingLoad = false;
    // The activity the inherited analysis was computed for; the inherited values only apply while the
    // current ride type still matches it (switching activity must re-evaluate the difficulty).
    let _inheritedRideType = null;

    let isShowElevation = $state(false);
    let elevationData = $state();
    let lastCoordinateElevations = $state();

    let isSectionColorMode = $state(false);
    let sectionPalette = $state(null);
    // Auto POI suggestions along the route: null = not queried, [] = none, [...] = results.

    let currentMapStyle = $state(MapTileService.getTileService() || MapTileService.layers.MAP_PLANNER);

    const PMAPS_STYLES = [MapTileService.layers.MAP_PLANNER, MapTileService.layers.MAP_PLANNER_OUTDOOR];
    const HIGHLIGHT_ELEMENTS = ['parking', 'viewpoint', 'drinking_water', 'peak', 'refuge', 'shelter', 'camp_site', 'pass'];

    const isPmapsStyle = $derived(PMAPS_STYLES.includes(currentMapStyle));
    const highlightableElements = $derived(isPmapsStyle ? HIGHLIGHT_ELEMENTS : []);
    const showWaymarkedTrails = $derived(isPmapsStyle && $mapStore.currentRideType === 'hiking');
    // Path-preference (avoid car roads) applies to foot activities; routing-only, so no style gating.
    const showPreferPaths = $derived(['hiking', 'walk', 'running'].includes($mapStore.currentRideType));

    // Publish the base style so panels outside the map (planning tools) can gate on it.
    $effect(() => {
        mapStore.currentMapStyle = currentMapStyle;
    });

    const isRedoAvailable = $derived.by(() => {
        void $mapStore.currentDistance;

        return mapStore.map?.canRedo ? mapStore.map.canRedo() : false;
    });

    const activeMode = $derived.by(() => {
        if (isSectionColorMode) {
            return {key: 'section', label: I18n.t('js.maps.toolbar.sections.title'), hint: I18n.t('js.maps.toolbar.sections.hint')};
        }

        if ($mapStore.isPoiModeEnabled) {
            return {key: 'poi', label: I18n.t('js.maps.toolbar.poi_mode.title'), hint: I18n.t('js.maps.toolbar.poi_mode.hint')};
        }

        if ($mapStore.isWaypointModeEnabled) {
            return {key: 'waypoint', label: I18n.t('js.maps.toolbar.waypoint_mode.title'), hint: I18n.t('js.maps.toolbar.waypoint_mode.hint')};
        }

        if ($mapStore.isFreehandDrawEnabled) {
            return {key: 'freehand', label: I18n.t('js.maps.toolbar.freehand_draw.title'), hint: I18n.t('js.maps.toolbar.freehand_draw.desc')};
        }

        return null;
    });

    const surfaceBreakdown = $derived(
        $mapStore.isSurfaceTypeEnabled && $mapStore.currentDistance && mapStore.map?.getSurfaceBreakdown
            ? mapStore.map.getSurfaceBreakdown()
            : []
    );

    const climbs = $derived.by(() => {
        if ($mapStore.currentRideType !== 'bike' || !lastCoordinateElevations?.length) {
            return [];
        }

        return Computation.detectClimbs(lastCoordinateElevations);
    });

    const technicity = $derived.by(() => {
        if (_inheritedAnalysis?.technicity && _inheritedRideType === $mapStore.currentRideType) {
            return _inheritedAnalysis.technicity;
        }

        if ($mapStore.currentRideType !== 'hiking' || !lastCoordinateElevations?.length) {
            return null;
        }

        const breakdown = surfaceBreakdown.length
            ? surfaceBreakdown
            : (mapStore.map?.getSurfaceBreakdown ? mapStore.map.getSurfaceBreakdown() : []);
        const paved = breakdown.find((entry) => entry.group === 'paved');
        const unpavedRatio = breakdown.length ? Math.max(0, (100 - (paved?.percent || 0)) / 100) : null;

        return Computation.estimateTechnicity(lastCoordinateElevations, unpavedRatio);
    });

    $effect(() => {
        const next = technicity;

        untrack(() => {
            if (JSON.stringify(mapStore.technicity) !== JSON.stringify(next)) {
                mapStore.technicity = next;
            }
        });
    });

    $effect(() => {
        const duration = $mapStore.currentDuration;
        const distance = $mapStore.currentDistance;

        if (!duration || !distance) {
            return;
        }

        const positiveElevation = ElevationRideTypes.includes($mapStore.currentRideType)
            ? ($mapStore.elevationApsis?.positiveElevation || 0)
            : 0;

        const calories = Computation.estimateCalories($mapStore.currentRideType, duration, distance, positiveElevation, _getUserWeight());

        if (calories !== mapStore.currentCalories) {
            mapStore.currentCalories = calories;
        }
    });

    const _initialMapPoints = () => {
        if (initialMapPoints) {
            if (Utils.is().isString(initialMapPoints)) {
                const stringPoints = initialMapPoints.split(';');
                return stringPoints.map((stringPoint) => {
                    const point = stringPoint.split(',');

                    if (point.length > 2) {
                        return {
                            type: point[0],
                            latitude: parseFloat(point[1]),
                            longitude: parseFloat(point[2]),
                            delta_distance: point[4] && parseFloat(point[3]),
                            delta_time: point[4] && parseFloat(point[4]),
                            delta_pause: point[5] && parseFloat(point[5])
                        };
                    } else {
                        return null;
                    }
                }).compact();
            } else {
                return initialMapPoints;
            }
        } else {
            return null;
        }
    };

    const _computeElevation = () => {
        if (!mapStore.map || !mapStore.currentDistance) {
            return;
        }

        if (typeof mapStore.map.computeElevation !== 'function') {
            if (_elevationModuleRetried) {
                return;
            }
            _elevationModuleRetried = true;

            MapBuilder.ensureModule(MAP_MODULES.ELEVATION_COMPUTATION)
                .then(() => _computeElevation())
                .catch(() => null);

            return;
        }

        mapStore.map.computeElevation((coordinateElevations, simplifiedCoordinateElevations) => {
            const elevationApsis = Computation.computeElevationApsis(simplifiedCoordinateElevations.map((c) => c[1]));

            const totalDistance = simplifiedCoordinateElevations[simplifiedCoordinateElevations.length - 1]?.[0] - simplifiedCoordinateElevations[0]?.[0];
            if (totalDistance > 0) {
                const totalElevationChange = simplifiedCoordinateElevations.reduce((sum, curr, idx, arr) => {
                    if (idx === 0) return 0;
                    return sum + Math.abs(curr[1] - arr[idx - 1][1]);
                }, 0);
                elevationApsis.gradeAvg = Math.round(totalElevationChange / totalDistance * 1000) / 10;
            }

            mapStore.elevationApsis = elevationApsis;

            // Keep the inherited stored level for a freshly loaded copy (while still on its own activity);
            // recompute once the user edits the track or switches activity. Re-apply the inherited value
            // (rather than just skip) so switching activity away and back restores it correctly.
            if (_inheritedAnalysis?.level && _inheritedRideType === mapStore.currentRideType) {
                mapStore.currentLevel = _inheritedAnalysis.level;
            } else {
                mapStore.currentLevel = RideLevels[Computation.estimateLevel(mapStore.currentRideType, mapStore.currentDistance, coordinateElevations.map((coordEl) => [coordEl.deltaDistance, coordEl.elevation]))];
            }

            if (ElevationRideTypes.includes(mapStore.currentRideType)) {
                mapStore.currentCalories = Computation.estimateCalories(mapStore.currentRideType, mapStore.currentDuration, mapStore.currentDistance, elevationApsis.positiveElevation, _getUserWeight());
            } else {
                mapStore.currentCalories = Computation.estimateCalories(mapStore.currentRideType, mapStore.currentDuration, mapStore.currentDistance, 0, _getUserWeight());
            }

            mapStore.map.updatePointsWithElevation(coordinateElevations);

            lastCoordinateElevations = coordinateElevations;
            // Publish for the local duration estimate (MapDataOutline / estimateRideDurationLocally). The
            // consumer (Computation.estimateServerDuration) smooths this profile before computing slopes to
            // match the server (which LOWESS-smooths elevation before slope binning).
            mapStore.coordinateElevations = coordinateElevations;
            mapStore.map.applySlopePercentageIfEnabled(coordinateElevations);

            if (coordinateElevations.length) {
                elevationData = {
                    positiveElevation: mapStore.elevationApsis.positiveElevation,
                    negativeElevation: mapStore.elevationApsis.negativeElevation,
                    minimumAltitude: mapStore.elevationApsis.minimumAltitude,
                    maximumAltitude: mapStore.elevationApsis.maximumAltitude,
                    charts: {
                        elevation: true,
                        speed: false,
                        pace: false
                    },
                    elevation: {
                        xUnits: 'km',
                        yUnits: 'm'
                    },
                    points: coordinateElevations.map((coordEl) => ({
                        distance: coordEl.totalDistance / 1000,
                        elevation: coordEl.elevation,
                        lat: coordEl.lat,
                        lng: coordEl.lng
                    }))
                };
            }
        });
    };

    const _handleMapDistanceChange = (newDistanceInKm) => {
        if (!_previousMapDistance && !!newDistanceInKm) {
            if (onPathStarted) {
                onPathStarted();
            } else if (mapId === 'home-map') {
                AnalyticsService.trackHomePathStarted();
            }
        }

        const formattedDistance = Math.round(newDistanceInKm * 1000);

        if (mapStore.currentDistance === formattedDistance) {
            return;
        }

        // The first real distance change after inheriting is the copy's own load — let it through;
        // any later change is a genuine edit, so drop the inherited values and recompute from now on.
        if (_inheritedAnalysis) {
            if (_inheritedAwaitingLoad) {
                _inheritedAwaitingLoad = false;
            } else {
                _inheritedAnalysis = null;
            }
        }

        if (newDistanceInKm === 0) {
            mapStore.durationComputed = true;
            mapStore.currentDuration = 0;
            mapStore.speedComputed = true;
            mapStore.currentSpeed = 0;
            mapStore.currentLevel = undefined;
            mapStore.coordinateElevations = null;

            if (ElevationRideTypes.includes(mapStore.currentRideType)) {
                mapStore.elevationApsis = {
                    positiveElevation: null,
                    negativeElevation: null,
                    minimumAltitude: null,
                    maximumAltitude: null,
                    gradeAvg: null
                };
            }
        }

        if (onDistanceChange) {
            onDistanceChange(formattedDistance);
        } else {
            mapStore.currentDistance = formattedDistance;

            if (mapStore.currentDistance) {
                if (mapStore.currentDistance && _previousMapDistance !== mapStore.currentDistance) {
                    _previousMapDistance = mapStore.currentDistance;
                }
            }
        }

        _computeElevation();
    };

    // Bumped per request so a slow COMPUTE_ROAD import cannot apply a stale intent after the user
    // changed their mind: a rejected entry is dropped from the cache, so the await can span three
    // import attempts plus backoff.
    let _snapToRoadRequestId = 0;

    const _handleSnapToRoadChange = async (checked) => {
        if (!mapStore.map) {
            return;
        }

        const requestId = ++_snapToRoadRequestId;

        if (checked) {
            // Report a failed import, then arm anyway: the call sites fall back to straight segments,
            // so the toggle stays truthful instead of silently disagreeing with the map.
            await MapBuilder.ensureModule(MAP_MODULES.COMPUTE_ROAD)
                .catch((error) => pushError(error));

            // The user may have unticked, or left the page, while that was pending.
            if (requestId !== _snapToRoadRequestId || !mapStore.map) {
                return;
            }
        }

        mapStore.map?.setSnapToRoad?.(checked);
    };

    const _prefetchAdvancedModules = () => {
        MapBuilder.ensureModules([MAP_MODULES.FREEHAND, MAP_MODULES.RIDE_POIS]);
    };

    const _handleFreehandDrawChange = async (checked) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.isFreehandDrawEnabled = checked;

        if (checked) {
            // Reporting matters: this used to reach Sentry as an unhandled rejection, and swallowing
            // it would leave a 404'd chunk completely silent.
            await MapBuilder.ensureModules([MAP_MODULES.FREEHAND, MAP_MODULES.COMPUTE_ROAD])
                .catch((error) => pushError(error));

            if (!mapStore.map) {
                return;
            }
        }

        mapStore.map?.setFreehandDraw?.(checked);
    };

    // Section-colour consumes a click on the track, so it must be off before a POI/waypoint mode
    // takes the click.
    const _disableSectionColorMode = () => {
        if (isSectionColorMode) {
            isSectionColorMode = false;
            sectionPalette = null;
            mapStore.map?.toggleSectionColorMode(false);
        }
    };

    const _disablePoiModes = () => {
        if ($mapStore.isPoiModeEnabled) {
            _handlePoiModeChange(false);
        }

        if ($mapStore.isWaypointModeEnabled) {
            _handleWaypointModeChange(false);
        }
    };

    const _handlePoiModeChange = async (checked) => {
        if (!mapStore.map) {
            return;
        }

        // Only one click-consuming mode at a time.
        if (checked) {
            if ($mapStore.isWaypointModeEnabled) {
                _handleWaypointModeChange(false);
            }
            _disableSectionColorMode();
        }

        mapStore.isPoiModeEnabled = checked;

        if (checked) {
            await MapBuilder.ensureModule(MAP_MODULES.RIDE_POIS);
            // RIDE_POIS is deferred for a ride without an id (RideNew): tell the POI editors to wire
            // their now-available on-map callbacks, otherwise the first click is dropped.
            window.dispatchEvent(new CustomEvent('mapplanner:poi-module-ready'));
        }

        mapStore.map.togglePoiMode?.(checked);

        if (checked) {
            Notification.info(I18n.t('js.maps.toolbar.poi_mode.hint'));
            AnalyticsService.trackImagePoiModeUsed();
        }
    };

    const _handleWaypointModeChange = async (checked) => {
        if (!mapStore.map) {
            return;
        }

        // Only one click-consuming mode at a time.
        if (checked) {
            if ($mapStore.isPoiModeEnabled) {
                _handlePoiModeChange(false);
            }
            _disableSectionColorMode();
        }

        mapStore.isWaypointModeEnabled = checked;

        if (checked) {
            await MapBuilder.ensureModule(MAP_MODULES.RIDE_POIS);
            window.dispatchEvent(new CustomEvent('mapplanner:poi-module-ready'));
        }

        mapStore.map.toggleWaypointMode?.(checked);

        if (checked) {
            Notification.info(I18n.t('js.maps.toolbar.waypoint_mode.hint'));
            AnalyticsService.trackWaypointModeUsed();
        }
    };

    const _handleStartPointChange = (coordinates) => {
        mapStore.startPointLocation = coordinates;
    };

    const _handleRouteTypeChange = (routeType) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.currentRideType = RouteTypeToRideType[routeType];
        mapStore.currentRouteType = routeType;

        // The waymarked-trails overlay is hiking-only; drop it when switching to another activity.
        if (mapStore.currentRideType !== 'hiking' && mapStore.isWaymarkedTrailsEnabled) {
            mapStore.isWaymarkedTrailsEnabled = false;
            mapStore.map.setWaymarkedTrailsMode(false);
        }

        mapStore.map.setRouteType(routeType);

        AnalyticsService.trackRouteTypeUsed();
    };

    const _handleBackToHomeClick = () => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.backToHome();
    };

    const _handleUndoClick = () => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.undo();
    };

    const _handleRedoClick = () => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.redo();
    };

    // Exit the currently active map-click mode (banner button / Esc key).
    const _handleExitActiveMode = () => {
        if (!activeMode) {
            return;
        }

        if (activeMode.key === 'section') {
            _handleSectionColorToggle();
        } else if (activeMode.key === 'poi') {
            _handlePoiModeChange(false);
        } else if (activeMode.key === 'waypoint') {
            _handleWaypointModeChange(false);
        } else if (activeMode.key === 'freehand') {
            _handleFreehandDrawChange(false);
        }
    };

    const _handleWindowKeydown = (event) => {
        if (event.key === 'Escape' && activeMode) {
            _handleExitActiveMode();
            return;
        }

        // Undo exists as a toolbar button; on desktop the hand reaches for Ctrl/Cmd+Z first.
        const historyAction = undoActionForKeydown(event, {
            // undo() clears the whole route when there is no snapshot to revert to.
            canUndo: mapStore.map?.canUndo?.() !== false,
            // Something is layered over the map: don't reshape the route behind it.
            blocked: !!document.querySelector('[role="dialog"], .swal2-container')
        });
        if (!historyAction) {
            return;
        }

        event.preventDefault();

        if (historyAction === 'undo') {
            _handleUndoClick();
        } else {
            _handleRedoClick();
        }
    };

    const _handleReverseClick = () => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.reverseRoute();

        AnalyticsService.trackRouteReversed();
    };

    const _handleOutAndBackClick = () => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.outAndBack();

        AnalyticsService.trackRouteOutAndBack();
    };

    const _handleSectionColorToggle = () => {
        if (!mapStore.map) {
            return;
        }

        isSectionColorMode = !isSectionColorMode;

        if (isSectionColorMode) {
            _disablePoiModes();
        } else {
            sectionPalette = null;
        }

        mapStore.map.toggleSectionColorMode(isSectionColorMode);

        if (isSectionColorMode) {
            Notification.info(I18n.t('js.maps.toolbar.sections.hint'));
        }
    };

    const _closeSectionPalette = () => {
        sectionPalette = null;
    };

    const _handleSectionColorPick = (color) => {
        if (!mapStore.map || !sectionPalette) {
            return;
        }

        // Picking the default colour resets the section to the track's colour (no override).
        const resolvedColor = color === defaultSectionColor ? null : color;
        mapStore.map.setSectionColor(sectionPalette.from, sectionPalette.to, resolvedColor);

        if (resolvedColor) {
            AnalyticsService.trackSectionColorSet();
        }

        sectionPalette = null;
    };

    const _handleClearClick = () => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.clearMap(() => {
            isShowElevation = false;
            elevationData = undefined;
            lastCoordinateElevations = undefined;
            mapStore.isSlopePercentageEnabled = false;
            mapStore.isSurfaceTypeEnabled = false;

            // Reset the edit toggles so their buttons don't stay active on an empty map.
            isSectionColorMode = false;
            sectionPalette = null;
            _disablePoiModes();

            StorageService.removeItem(TIME_STORAGE_KEY);

            window.dispatchEvent(new CustomEvent('mapplanner:map-cleared'));
        }, {
            noPopup: userStore.isConnected
        });
    };

    const _handleSlopePercentageChange = (checked) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.isSlopePercentageEnabled = checked;
        if (checked) {
            mapStore.isSurfaceTypeEnabled = false;
        }
        mapStore.map.toggleSlopePercentage(checked, lastCoordinateElevations);
    };

    const _handleSurfaceTypeChange = (checked) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.isSurfaceTypeEnabled = checked;
        if (checked) {
            mapStore.isSlopePercentageEnabled = false;
        }
        mapStore.map.toggleSurfaceType(checked);
    };

    const _handleWaymarkedTrailsChange = (checked) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.isWaymarkedTrailsEnabled = checked;
        mapStore.map.setWaymarkedTrailsMode(checked);
    };

    const _handlePreferPathsChange = (checked) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.isPreferPathsEnabled = checked;
        mapStore.map.setPreferPathsMode(checked);
    };

    const _handleMapStyleChange = (mapStyle) => {
        if (!mapStore.map) {
            return;
        }

        currentMapStyle = mapStyle;

        // Waymarked trails need the MapPlanner tiles to render; on any other basemap its toggle is hidden,
        // so drop it entirely (display + routing) rather than leave it silently constraining the route.
        if (!PMAPS_STYLES.includes(mapStyle) && mapStore.isWaymarkedTrailsEnabled) {
            mapStore.isWaymarkedTrailsEnabled = false;
            mapStore.map.setWaymarkedTrailsMode(false);
        }

        mapStore.map.setMapStyle(mapStyle);
    };

    const _handleHighlightMapElement = (element) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.highlightMapElement(element);

        AnalyticsService.trackHighlightUsed();
    };

    const _handleFontSizeChange = (delta) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.changeMapFontSize(delta);
    };

    const _handleElevationClick = (checked) => {
        if (!mapStore.map) {
            return;
        }

        const newValue = typeof checked === 'boolean' ? checked : !isShowElevation;

        if (window.isMetricsEnabled && newValue) {
            AnalyticsService.trackComputeElevationClick('Home page');
        }

        isShowElevation = newValue;
    };

    const _setMapLocation = (bbox, center, placeType, name) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.goToLocation(bbox, center, name, {
            showPositionMarker: !name,
            // Use address our street or user geolocation as starting point
            useStartMarker: ['address', 'street', 'geolocation', 'house'].includes(placeType)
        });
    };

    const _hasSharedRideParam = () => {
        const params = new URLSearchParams(window.location.search);
        return params.has('share') || params.has('ride_id');
    };

    const _loadHomeMap = () => {
        MapBuilder.createMap(
            MAP_CLASS.BUILD,
            [
                MAP_MODULES.TOOLBAR,
                MAP_MODULES.COMPUTE_ROAD,
                MAP_MODULES.DRAW_LINE,
                MAP_MODULES.FREEHAND,
                MAP_MODULES.RIDE_STORE,
                MAP_MODULES.RIDE_SHARE,
                MAP_MODULES.RIDE_POIS,
                MAP_MODULES.ELEVATION_COMPUTATION,
                MAP_MODULES.GRAPH,
                MAP_MODULES.GEOLOCATION,
                ...extraModules
            ],
            {
                mapId,
                rideId,
                reusePreviousTracks,
                initialSections,
                initialSurfaces,
                initialPoints: _initialMapPoints(),
                initialLocation: initialMarkerData ? [initialMarkerData.lat, initialMarkerData.lng] : mapStore.mapLocation,
                reusePosition: !initialMarkerData && !mapStore.mapLocation,
                highlightedPlaceName: initialMarkerData?.name,
                useSearchMarker: !!initialMarkerData,
                hideMapScale: true,
                defaultZoom: 12
            },
            {
                deferredModules: [
                    ...(rideId ? [MAP_MODULES.FREEHAND] : [MAP_MODULES.FREEHAND, MAP_MODULES.RIDE_POIS]),
                    // RIDE_SHARE only loads a shared/copied ride at boot (getSharedRide reads ?share/
                    // ?ride_id). With neither param it does nothing, so keep it off the initial load
                    // of the common no-param visit and let ensureModule fetch it only when needed.
                    ...(_hasSharedRideParam() ? [] : [MAP_MODULES.RIDE_SHARE]),
                    ...deferredExtraModules
                ]
            }
        )
            .then((map) => {
                if (!map) {
                    return;
                }

                mapStore.map = map;

                mapStore.map.setRouteType(mapStore.currentRouteType);

                mapStore.map.setPreferPathsMode(mapStore.isPreferPathsEnabled);

                mapStore.map.onDistanceUpdated(_handleMapDistanceChange);

                mapStore.map.onStartPointChanged(_handleStartPointChange);

                mapStore.map.onSectionClick((payload) => {
                    sectionPalette = payload;
                });

                mapStore.map.onMapLoaded(() => {
                    if (_hasInitialElevation || !mapStore.currentDistance || !mapStore.map.hasActivityData()) {
                        return;
                    }

                    _hasInitialElevation = true;

                    _computeElevation();

                    if (!mapStore.startPointLocation) {
                        const firstPoint = mapStore.map._mapModel?.linePoints?.[0]?.latLng;
                        if (firstPoint) {
                            mapStore.startPointLocation = firstPoint;
                        }
                    }
                });

                userStore.subscribe((userState) => {
                    if (userState.unit !== _previousUnit) {
                        _previousUnit = userState.unit;

                        mapStore.map.onUnitChanged(userState.unit);
                    }
                });
            });
    };

    // The multi-day tool offers a "place stops manually" shortcut that arms waypoint mode from outside
    // the toolbar — reuse the same handler so RIDE_POIS loads and the placement hint shows.
    const _handleEnableWaypointModeEvent = () => _handleWaypointModeChange(true);

    // A shared/copied ride carries its own activity + stored difficulty/technicity: adopt them so the
    // route-type bar, the save-form default and the details panel match the loaded ride (and its ride-show
    // page) instead of the visitor default / a fresh client recompute — until the track is edited.
    const _handleRideLoaded = (event) => {
        const {rideType, level, technicity} = event.detail || {};

        if (rideType && rideType !== mapStore.currentRideType) {
            mapStore.currentRideType = rideType;
        }

        if (level || technicity) {
            _inheritedAnalysis = {level: level || null, technicity: technicity || null};
            _inheritedRideType = rideType || mapStore.currentRideType;
            // The copy's own track load will settle the distance once — don't treat that as an edit.
            _inheritedAwaitingLoad = true;

            if (level) {
                mapStore.currentLevel = level;
            }
            if (technicity) {
                mapStore.technicity = technicity;
            }
        }
    };

    onMount(() => {
        if (!mapStore.map) {
            _loadHomeMap();
        }

        window.addEventListener('mapplanner:enable-waypoint-mode', _handleEnableWaypointModeEvent);
        window.addEventListener('mapplanner:ride-loaded', _handleRideLoaded);

        return () => {
            window.removeEventListener('mapplanner:enable-waypoint-mode', _handleEnableWaypointModeEvent);
            window.removeEventListener('mapplanner:ride-loaded', _handleRideLoaded);

            if (mapStore.map) {
                mapStore.map.destroy();
                mapStore.map = null;
            }
        };
    });

    $effect(() => {
        if ($mapStore.mapLocation && ($mapStore.mapLocation.bbox || $mapStore.mapLocation.center) && _previousMapLocationValue !== $mapStore.mapLocation.value) {
            _previousMapLocationValue = $mapStore.mapLocation.value;

            _setMapLocation($mapStore.mapLocation.bbox, $mapStore.mapLocation.center, $mapStore.mapLocation.placeType, $mapStore.mapLocation.name);
        }
    });

    $effect(() => {
        if ($mapStore.currentRideType && _previousRideType !== $mapStore.currentRideType) {
            _previousRideType = $mapStore.currentRideType;

            // Reset path-preference to its per-activity default (on for hiking, off otherwise).
            mapStore.isPreferPathsEnabled = $mapStore.currentRideType === 'hiking';
            mapStore.map?.setPreferPathsMode(mapStore.isPreferPathsEnabled);

            _computeElevation();
        }
    });

    $effect(() => {
        if ($mapStore.currentRouteType && _previousRouteType !== $mapStore.currentRouteType) {
            _previousRouteType = $mapStore.currentRouteType;

            mapStore.map?.setRouteType($mapStore.currentRouteType);
        }
    });

    $effect(() => {
        const rideType = $mapStore.currentRideType;
        const unit = $userStore.unit;

        mapStore.map?.setMaxDistance(resolveMaxDistanceMeters(userStore.featureFlags.map_max_distance, rideType, unit));
    });

</script>

<svelte:window onkeydown={_handleWindowKeydown}/>

<div id={mapId}
     class="my-4 relative">
    {@render mapExtras?.()}
    <div class="map-area relative overflow-visible">
        <div id={`${mapId}-map`}
             class={classnames('map-canvas relative', className)}>
            <MapToolbar isSnapToRoadChecked={$userStore.featureFlags.map_snap_to_road_enabled}
                        maxRouteTypeChange={$userStore.featureFlags.map_max_route_type}
                        useRouteType={useRouteType}
                        isPoiModeChecked={$mapStore.isPoiModeEnabled}
                        onPoiModeChange={_handlePoiModeChange}
                        isWaypointModeChecked={$mapStore.isWaypointModeEnabled}
                        onWaypointModeChange={_handleWaypointModeChange}
                        hasElevationData={!!lastCoordinateElevations?.length}
                        isSlopePercentageEnabled={$mapStore.isSlopePercentageEnabled}
                        onSlopePercentageChange={_handleSlopePercentageChange}
                        isSurfaceTypeEnabled={$mapStore.isSurfaceTypeEnabled}
                        onSurfaceTypeChange={_handleSurfaceTypeChange}
                        isWaymarkedTrailsEnabled={$mapStore.isWaymarkedTrailsEnabled}
                        onWaymarkedTrailsChange={showWaymarkedTrails ? _handleWaymarkedTrailsChange : undefined}
                        isPreferPathsEnabled={$mapStore.isPreferPathsEnabled}
                        onPreferPathsChange={showPreferPaths ? _handlePreferPathsChange : undefined}
                        isElevationEnabled={isShowElevation}
                        onElevationToggle={_handleElevationClick}
                        onSnapToRoadChange={_handleSnapToRoadChange}
                        isFreehandDrawChecked={$mapStore.isFreehandDrawEnabled}
                        onFreehandDrawChange={_handleFreehandDrawChange}
                        onMenuOpen={_prefetchAdvancedModules}
                        onRouteTypeChange={_handleRouteTypeChange}
                        onBackToHomeClick={_handleBackToHomeClick}
                        onUndoClick={_handleUndoClick}
                        onRedoClick={_handleRedoClick}
                        isRedoAvailable={isRedoAvailable}
                        onReverseClick={_handleReverseClick}
                        onOutAndBackClick={_handleOutAndBackClick}
                        onSectionColorToggle={_handleSectionColorToggle}
                        isSectionColorActive={isSectionColorMode}
                        onHighlightMapElement={_handleHighlightMapElement}
                        mapElementHighlighted={highlightableElements}
                        onClearClick={_handleClearClick}/>

            {#if sectionPalette}
                {#await MapSectionPalette() then PaletteLoader}
                    {@const Palette = PaletteLoader.default}
                    <Palette x={sectionPalette.x}
                             y={sectionPalette.y}
                             currentColor={sectionPalette.color || defaultSectionColor}
                             colors={sectionColors}
                             onPick={_handleSectionColorPick}
                             onClose={_closeSectionPalette}/>
                {/await}
            {/if}

            {#if $userStore.featureFlags.map_available_layers?.length}
                <MapLayers mapLayersAvailable={$userStore.featureFlags.map_available_layers}
                           onMapStyleChange={_handleMapStyleChange}
                           onFontSizeChange={_handleFontSizeChange}/>
            {/if}

            <MapTypeLegend visible={isPmapsStyle && !$mapStore.isSlopePercentageEnabled && !$mapStore.isSurfaceTypeEnabled}/>

            {#if activeMode}
                <MapModeBanner label={activeMode.label}
                               hint={activeMode.hint}
                               onExit={_handleExitActiveMode}/>
            {/if}

            <!-- The mode banner and the route-type bar share the top-centre slot; while a mode is
                 active the user is not choosing a route type, so hide the bar and show only the banner. -->
            {#if !activeMode}
                <MapRouteTypeBar/>
            {/if}

            {#if $mapStore.isSlopePercentageEnabled}
                <MapSlopeLegend/>
            {/if}

            {#if $mapStore.isSurfaceTypeEnabled}
                <MapSurfaceLegend breakdown={surfaceBreakdown}/>
            {/if}
        </div>

        <MapDataOutline estimateDuration={true}
                        addBreakTime={movingDuration}/>

        <MapLoader/>
    </div>

    <div class="mt-6"></div>

    <MapElevationComputation/>

    {#if isShowElevation}
        <div transition:slide={{duration: 300, easing: cubicInOut}}>
            <MapElevationGraph map={$mapStore.map}
                               currentRideType={$mapStore.currentRideType}
                               elevationData={elevationData}
                               warningHelper={true}/>
        </div>
    {/if}

    <MapElevationButton onElevationToggle={_handleElevationClick}/>

    <MapClimbs climbs={climbs}/>
</div>
